/**
 * How a refused sign-in is recognised and worded.
 *
 * A deactivated person's login is banned (migration 274), and gotrue refuses a
 * banned account on every way in — password, Microsoft, an email link — with
 * `user_banned` ("User is banned"). That sentence is accurate and wrong for a
 * colleague whose access an administrator removed: nobody should be told they
 * are "banned", and the Microsoft card's usual advice ("signing in again
 * usually clears this") would send them round a loop that cannot end.
 */

export interface AuthErrorLike {
  message?: string;
  code?: string;
  status?: number;
}

/** gotrue answers a banned account with the code `user_banned`; older
 *  clients carry only the sentence, so either is enough. */
export function isUserBannedError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const e = error as AuthErrorLike;
  return e.code === 'user_banned' || /user\s+is\s+banned/i.test(e.message ?? '');
}

/** What the sign-in form says when a deactivated person's password is refused. */
export const ACCESS_REMOVED_MESSAGE =
  'Your access to Flow has been removed. If you think that’s a mistake, contact an administrator.';

/** The sentence the password form shows for a failed sign-in. Everything but a
 *  ban keeps gotrue's own words, as before. */
export function signInErrorMessage(err: unknown): string {
  if (isUserBannedError(err)) return ACCESS_REMOVED_MESSAGE;
  return err instanceof Error ? err.message : 'An error occurred during sign in';
}
